'use client'

import React, { useCallback, useSyncExternalStore } from 'react'
import ActivityFeed from '@/app/activity/activity-feed'
import dynamic from 'next/dynamic'
import ActivityLoading from '@/components/activity-loading'
const ListingsFeed = dynamic(() => import('@/app/activity/listings-feed'), { loading: () => <ActivityLoading /> })
const HomeSocialFeed = dynamic(() => import('@/components/collection-social/home-feed'), { ssr: false, loading: () => <ActivityLoading /> })
const MempoolFeed = dynamic(() => import('@/app/activity/mempool-feed'), { loading: () => <ActivityLoading /> })
const BidsFeed = dynamic(() => import('@/app/activity/bids-feed'), { loading: () => <ActivityLoading /> })
import Image from 'next/image'


const STORAGE_KEY = 'activity-sidebar-tab'
// Same-tab localStorage writes don't emit a 'storage' event (that fires only in
// OTHER tabs), so writes dispatch this so our own subscriber re-renders.
const LOCAL_WRITE_EVENT = 'ow:local-storage-write'

/// localStorage-backed string state that is SSR-safe and lint-clean. During SSR
/// and the first client (hydration) render, useSyncExternalStore uses the server
/// snapshot (the default) so the markup matches; it switches to the persisted
/// value on the next render. No hydration mismatch, no setState-in-effect.
function useLocalStorageString<T extends string>(
	key: string,
	fallback: T,
	parse: (raw: string | null) => T
): [T, (next: T) => void] {
	const subscribe = useCallback(
		(onChange: () => void) => {
			const onStorage = (e: StorageEvent) => {
				if (e.key === key || e.key === null) onChange()
			}
			window.addEventListener('storage', onStorage)
			window.addEventListener(LOCAL_WRITE_EVENT, onChange)
			return () => {
				window.removeEventListener('storage', onStorage)
				window.removeEventListener(LOCAL_WRITE_EVENT, onChange)
			}
		},
		[key]
	)
	const value = useSyncExternalStore(
		subscribe,
		() => parse(localStorage.getItem(key)),
		() => fallback
	)
	const setValue = useCallback(
		(next: T) => {
			localStorage.setItem(key, next)
			window.dispatchEvent(new Event(LOCAL_WRITE_EVENT))
		},
		[key]
	)
	return [value, setValue]
}

const ActivitySidebar: React.FC<{
	open: boolean
	/** False once the drawer has slid shut: the panel leaves layout entirely. */
	shown?: boolean
	initialItems: any[]
	itemsCount: number
	activitySource?: 'local' | 'merged'
}> = ({ open, shown = true, initialItems, itemsCount, activitySource = 'local' }) => {
	const [tab, setTab] = useLocalStorageString<'sales' | 'listings' | 'mempool' | 'bids' | 'social'>(
		STORAGE_KEY,
		'sales',
		(raw) =>
			raw === 'mempool' || raw === 'listings' || raw === 'bids' || raw === 'social' ? raw : 'sales'
	)

	return (
		<aside aria-label="Market activity" aria-hidden={!open} inert={!open}
				id="market-activity-sidebar"
				className={`fixed bottom-8 right-0 top-[var(--ow-header-h)] z-40 ${shown ? 'flex' : 'hidden'} w-[360px] max-w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden border-l border-border bg-background p-0`}
			>
				<header className="border-b border-border px-5 py-4 pr-14 text-left">
					<div className="flex items-center gap-2">
						<Image src="/logo-icon.svg" alt="Ordinals Wallet" width={32} height={32} className="size-8 shrink-0" />
						<div>
							<h2>Market activity</h2>
							<p className="text-xs">
								Sales, listings, pending transactions, offers and theses
							</p>
						</div>
					</div>
				</header>

				<div className="flex border-b border-border" role="group" aria-label="Activity view">
     {(['sales', 'listings', 'bids', 'mempool', 'social'] as const).map(view => <button key={view} type="button" aria-pressed={tab === view} onClick={() => setTab(view)} className={`relative min-w-0 flex-1 whitespace-nowrap px-1 py-3 text-[11px] font-medium focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${tab === view ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
      {{ sales: 'Trades', listings: 'Listings', mempool: 'Mempool', bids: 'Offers', social: 'Social' }[view]}
      {tab === view && <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gradient-to-r from-purple-600 to-blue-500" />}
     </button>)}
    </div>

				<div className="min-h-0 flex-1 overflow-hidden">
					{tab === 'sales' ? (
							<ActivityFeed
								initialItems={initialItems}
								itemsCount={itemsCount}
								isFullPage={true}
								hideHeader
								activitySource={activitySource}
								paused={!open}
								windowRows
							/>
						) : tab === 'listings' ? <ListingsFeed hideHeader /> : tab === 'social' ? <HomeSocialFeed /> : tab === 'bids' ? (
							<BidsFeed />
						) : (
							<MempoolFeed hideHeader onSwitchTab={() => setTab('sales')} />
						)}
				</div>

			</aside>
	)
}

export default ActivitySidebar
